import { inject, Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

import { EMPTY, iif, Observable, switchMap, take } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { IEnvironment } from '../../models/infrastructure/environment.model';
import { AuthTokenService } from '../../infrastructure/authentication/auth-token.base-service';
import { getInvitationCodeResponse } from '../../+state/account/registration/registration.selectors';
import { getSelectedCustomerIdent } from '../../+state/account/user-apps/user-apps.selectors';

export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authTokenService = inject(AuthTokenService);
  private store = inject(Store);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private environment = inject<IEnvironment>('ENVIRONMENT' as any);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.indexOf('/graphql') > -1) {
      // Requests tagged with SKIP_AUTH bypass the token check (login, registration, downtime, etc.)
      if (request.context.get(SKIP_AUTH)) {
        return this.handleCustomerIdent(request, next);
      }

      return this.store.select(getSelectedCustomerIdent).pipe(
        take(1),
        tap(response => {
          const authToken = this.authTokenService.getToken();

          if (authToken?.accessToken) {
            request = request.clone({
              setHeaders: {
                Authorization: `Bearer ${String(authToken.accessToken)}`,
              },
            });
          }
          if (response) {
            request = request.clone({
              setHeaders: {
                'X-Customer-Ident': response,
              },
            });
          }
        }),
        switchMap(response => {
          const authToken = this.authTokenService.getToken();

          // Block authenticated requests that have no token
          if (!authToken?.accessToken) {
            console.warn('AuthInterceptor: Blocked GraphQL request — no auth token available');
            return EMPTY;
          }

          return iif(
            () => !!response,
            next.handle(request),
            this.store.select(getInvitationCodeResponse).pipe(
              take(1),
              switchMap(invitationResponse => {
                if (invitationResponse) {
                  request = request.clone({
                    setHeaders: {
                      'X-Customer-Ident': invitationResponse.customerIdent,
                    },
                  });
                } else if (this.environment.customerIdent) {
                  request = request.clone({
                    setHeaders: {
                      'X-Customer-Ident': this.environment.customerIdent,
                    },
                  });
                }
                return next.handle(request);
              })
            )
          );
        })
      );
    }

    return next.handle(request);
  }

  private handleCustomerIdent(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return this.store.select(getSelectedCustomerIdent).pipe(
      take(1),
      switchMap(customerIdent => {
        if (customerIdent) {
          request = request.clone({
            setHeaders: { 'X-Customer-Ident': customerIdent },
          });
          return next.handle(request);
        }

        return this.store.select(getInvitationCodeResponse).pipe(
          take(1),
          switchMap(invitationResponse => {
            if (invitationResponse) {
              request = request.clone({
                setHeaders: { 'X-Customer-Ident': invitationResponse.customerIdent },
              });
            } else if (this.environment.customerIdent) {
              request = request.clone({
                setHeaders: { 'X-Customer-Ident': this.environment.customerIdent },
              });
            }
            return next.handle(request);
          })
        );
      })
    );
  }
}
